import { useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { DataAdapter } from "@shared-core/data/adapter";
import type * as API from "@shared-core/data/api-types";
import { useAuth } from "../../../auth/hooks";

// Many TicketCards can mount at once (a list of dozens of rows), and each
// independently wants "is my own M365 connection usable" — without this,
// that's dozens of simultaneous identical GET m365/connection/health calls.
// Cached for the page session; a connect/disconnect done elsewhere (Settings)
// won't be reflected until reload, an acceptable staleness window for
// deciding which dialog a click opens.
let cachedHealthPromise: Promise<API.ConnectionHealthResponse[]> | null = null;

function fetchConnectionHealth(
  adapter: DataAdapter,
): Promise<API.ConnectionHealthResponse[]> {
  if (!cachedHealthPromise) {
    cachedHealthPromise = adapter.getM365ConnectionHealth().catch((err) => {
      cachedHealthPromise = null; // let the next mount retry after a failure
      throw err;
    });
  }
  return cachedHealthPromise;
}

/** Test-only: clears the module-level cache so each test starts from a
 * clean slate instead of reusing a previous test's resolved connection list. */
export function resetM365ConnectionStatusCacheForTests(): void {
  cachedHealthPromise = null;
}

export type M365ConnectionStatus = "loading" | "usable" | "not_connected";

// Mirrors the backend's actual push gate (push_ticket_to_outlook_calendar via
// get_graph_client_for_user): only a missing connection or one that is
// disconnected/broken_needs_reconnect is rejected server-side. backfilling/
// stale connections can still push — the mail/calendar delta sync's own
// health is a different concern from "can Graph create one event".
const USABLE_HEALTH_STATUSES = new Set<
  API.ConnectionHealthResponse["health_status"]
>(["healthy", "backfilling", "stale"]);

/**
 * Whether the current user's own M365 connection is usable enough to push a
 * ticket to their Outlook calendar right now. Drives which dialog "Add to
 * calendar" opens — the real push flow, or the "connect Microsoft 365" prompt.
 */
export function useM365ConnectionStatus(): M365ConnectionStatus {
  const adapter = useDataAdapter();
  const { user } = useAuth();
  const [status, setStatus] = useState<M365ConnectionStatus>("loading");

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetchConnectionHealth(adapter)
      .then((connections) => {
        if (cancelled) return;
        const mine = connections.find((c) => c.app_user_id === user.id);
        setStatus(
          mine && USABLE_HEALTH_STATUSES.has(mine.health_status)
            ? "usable"
            : "not_connected",
        );
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("not_connected");
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, user]);

  return status;
}
